import { useState } from "react";

import { cn } from "@/lib/utils";
import type { DetailWorkflow } from "@/services/navigator/agent-detail-service";

import { NvAnalyticsPicto, NvDatabaseIcon } from "./detail-icons";
import { DetailLabel, DetailSection } from "./detail-parts";
import { WorkflowDiagram, WorkflowFullButton, WorkflowFullDialog, WorkflowLegend } from "./workflow-diagram";

// 워크플로우 (설계서 slide 45~46 · Figma 03-01 · 03-04 · 03-05): 2차까지 · 1차+WF는 도식 + 입력 · 산출물 · 연동 시스템 + 도입 전 / 도입 후 탭,
// 1차만은 워크플로우 없이 도입 전후(After가 개요와 같아 Before만 남으면 '해결하는 문제', 문장 단위 카드 3개 이내).

type TabKey = "flow" | "before" | "after";

function TabSwitch({ tabs, active, onChange }: { tabs: { key: TabKey; label: string }[]; active: TabKey; onChange: (key: TabKey) => void }) {
  return (
    <div role="tablist" aria-label="워크플로우 보기" className="inline-flex gap-1 self-start rounded-nv-control bg-nv-bg-page p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={t.key === active}
          onClick={() => onChange(t.key)}
          className={cn("h-11 rounded-nv-control px-5 text-[15px] leading-6", t.key === active ? "bg-white text-nv-text-primary shadow-nv-card" : "text-nv-text-secondary hover:text-nv-text-primary")}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function IoBlocks({ data }: { data: DetailWorkflow }) {
  const hasIn = data.inputs.length > 0;
  const hasOut = data.outputs.length > 0;
  if (!hasIn && !hasOut) return null;
  return (
    <div className={cn("grid gap-6", hasIn && hasOut ? "lg:grid-cols-[minmax(0,1fr)_30px_minmax(0,1fr)] lg:gap-[9px]" : "grid-cols-1")}>
      {hasIn && (
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="text-[15px] leading-6 text-nv-text-primary">입력 데이터</h3>
          {/* 입력 · 산출물 두 목록은 같은 높이로, 항목은 남는 높이를 나눠 가져요 (Figma ver.7 03-01) */}
          <ul className="flex flex-1 flex-col overflow-hidden rounded-nv-card border border-nv-border bg-nv-surface">
            {data.inputs.map((x, i) => (
              <li key={`${x.name}-${i}`} className={cn("flex flex-1 flex-col gap-[6px] px-4 py-4", i > 0 && "border-t border-nv-border")}>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-[15px] font-medium leading-6 text-nv-text-primary">{x.name}</span>
                  {x.required && <span className={cn("shrink-0 text-[12px] leading-5", x.required === "필수" ? "text-nv-brand-text" : "text-nv-text-tertiary")}>{x.required}</span>}
                </div>
                {x.format && <span className="text-[13px] leading-[21px] text-nv-text-secondary">{x.format}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasIn && hasOut && (
        <div aria-hidden className="hidden items-center justify-center text-nv-text-placeholder lg:flex">
          <svg width="29" height="12" viewBox="0 0 29 12" fill="none">
            <path d="M0.763158 5.85088H28.2368M23.1491 10.9386L28.2368 5.85088L23.1491 0.763158" stroke="currentColor" strokeWidth="1.52632" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
      {hasOut && (
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="text-[15px] leading-6 text-nv-text-primary">산출물</h3>
          <ul className="flex flex-1 flex-col overflow-hidden rounded-nv-card border border-[#F3D7C8] bg-[#FFF7F2]">
            {data.outputs.map((x, i) => (
              <li key={`${x.name}-${i}`} className={cn("flex flex-1 flex-col gap-[6px] px-4 py-4", i > 0 && "border-t border-[#F3D7C8]")}>
                <span className="text-[15px] font-medium leading-6 text-nv-text-primary">{x.name}</span>
                {x.content && <span className="text-[13px] leading-[21px] text-nv-text-secondary">{x.content}</span>}
                {x.format && <span className="text-[13px] leading-[21px] text-nv-text-tertiary">{x.format}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// 받는 데이터(1차+WF · 실선 + 데이터베이스 아이콘) · 연동 시스템(점선 + 분석 아이콘) 카드 (Figma ver.7 03-01 · 03-04 'sys': 반경 16, 아이콘 타일 56)
function SysCard({ label, text, icon, dashed }: { label: string; text: string; icon: React.ReactNode; dashed?: boolean }) {
  return (
    <div className={cn("flex items-center gap-4 rounded-[16px] border bg-nv-surface p-6", dashed ? "border-dashed border-nv-border-strong" : "border-nv-border")}>
      <span aria-hidden className="relative hidden h-14 w-14 shrink-0 items-center justify-center rounded-[16px] bg-nv-bg-page sm:flex">
        {icon}
      </span>
      <div className="flex min-w-0 flex-col gap-[6px]">
        <span className="text-[13px] font-medium leading-[21px] text-nv-text-tertiary">{label}</span>
        <p className="whitespace-pre-line text-[15px] leading-6 text-nv-text-secondary">{text}</p>
      </div>
    </div>
  );
}

function PanelBox({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-6 rounded-[20px] bg-nv-bg-page p-5 xl:p-7">{children}</div>;
}

function WhiteCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("rounded-nv-card border border-nv-border bg-nv-surface p-5", className)}>{children}</div>;
}

function BeforePanel({ before }: { before: NonNullable<DetailWorkflow["before"]> }) {
  if (before.problem) {
    return (
      <PanelBox>
        <p className="whitespace-pre-line text-[16px] leading-7 text-nv-text-secondary">{before.problem}</p>
      </PanelBox>
    );
  }
  return (
    <PanelBox>
      {before.summary && <p className="text-[17px] leading-[30px] text-nv-text-primary">{before.summary}</p>}
      {before.steps?.map((group, g) => (
        <div key={g} className="flex flex-col gap-3">
          {group.stage && <DetailLabel>{group.stage}</DetailLabel>}
          <ol className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {group.items.map((item, i) => (
              <li key={i}>
                <WhiteCard className="flex h-full flex-col gap-1">
                  <span className="flex items-baseline gap-2 text-[15px] font-medium leading-6 text-nv-text-primary">
                    {item.no != null && <span className="text-nv-brand-text">{item.no}</span>}
                    {item.title}
                  </span>
                  <span className="text-[14px] leading-[22px] text-nv-text-secondary">{item.text}</span>
                </WhiteCard>
              </li>
            ))}
          </ol>
        </div>
      ))}
      {before.pain_points && before.pain_points.length > 0 && (
        <div className="flex flex-col gap-3">
          <DetailLabel>Pain Point</DetailLabel>
          <ul className="grid gap-3 md:grid-cols-2">
            {before.pain_points.map((p, i) => (
              <li key={i}>
                <WhiteCard className="flex h-full gap-3 text-[14px] leading-[22px] text-nv-text-secondary">
                  <span aria-hidden className="mt-[9px] h-[6px] w-[6px] shrink-0 rounded-full bg-nv-brand" />
                  {p}
                </WhiteCard>
              </li>
            ))}
          </ul>
        </div>
      )}
    </PanelBox>
  );
}

function AfterPanel({ after }: { after: NonNullable<DetailWorkflow["after"]> }) {
  if (after.solution) {
    return (
      <PanelBox>
        <p className="whitespace-pre-line text-[16px] leading-7 text-nv-text-secondary">{after.solution}</p>
      </PanelBox>
    );
  }
  const scope = [
    { label: "자동화 수준", value: after.automation },
    { label: "Agent 대체 범위", value: after.agent_scope },
    { label: "사람 유지 범위", value: after.human_scope },
  ].filter((r): r is { label: string; value: string } => !!r.value);
  return (
    <PanelBox>
      {scope.length > 0 && (
        <div className="grid gap-3 md:grid-cols-3">
          {scope.map((r) => (
            <WhiteCard key={r.label} className="flex flex-col gap-1">
              <DetailLabel>{r.label}</DetailLabel>
              <p className="text-[15px] leading-6 text-nv-text-primary">{r.value}</p>
            </WhiteCard>
          ))}
        </div>
      )}
      {after.changes && after.changes.length > 0 && (
        <div className="flex flex-col gap-3">
          <DetailLabel>기대 변화</DetailLabel>
          <div className="grid gap-3 md:grid-cols-3">
            {after.changes.map((c, i) => (
              <WhiteCard key={i} className="flex flex-col gap-1">
                {c.label && <span className="text-[13px] font-bold leading-[21px] text-nv-brand-text">{c.label}</span>}
                <p className="text-[15px] leading-6 text-nv-text-primary">{c.text}</p>
              </WhiteCard>
            ))}
          </div>
        </div>
      )}
    </PanelBox>
  );
}

// 1차만: 워크플로우 도식 없이 도입 전후 (문장 단위 카드, 카드 제목 없음, 카드 높이 통일)
function BeforeAfterOnly({ data }: { data: DetailWorkflow }) {
  const cards = data.before?.cards ?? [];
  return (
    <div className="flex flex-col gap-8">
      {cards.length > 0 && (
        <div className="flex flex-col gap-4">
          {data.after && <h3 className="text-[20px] font-medium leading-8 text-nv-text-primary">도입 전</h3>}
          <div className="grid auto-rows-fr gap-6 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((c, i) => (
              <p key={i} className="break-keep rounded-[16px] bg-[#F7F8FA] p-7 text-[16px] leading-7 text-nv-text-secondary">
                {c}
              </p>
            ))}
          </div>
        </div>
      )}
      {data.after?.solution && (
        <div className="flex flex-col gap-4">
          <h3 className="text-[20px] font-medium leading-8 text-nv-text-primary">도입 후</h3>
          <p className="whitespace-pre-line rounded-[16px] bg-nv-brand-subtle p-7 text-[16px] leading-7 text-nv-text-primary">{data.after.solution}</p>
        </div>
      )}
    </div>
  );
}

export function WorkflowSection({ data, agentName }: { data: DetailWorkflow; agentName: string }) {
  const hasFlow = data.stages.length > 0;
  const tabs: { key: TabKey; label: string }[] = [
    ...(hasFlow ? [{ key: "flow" as const, label: "워크플로우" }] : []),
    ...(data.before && hasFlow ? [{ key: "before" as const, label: "도입 전" }] : []),
    ...(data.after && hasFlow ? [{ key: "after" as const, label: "도입 후" }] : []),
  ];
  const [tab, setTab] = useState<TabKey>(tabs[0]?.key ?? "flow");
  const [full, setFull] = useState(false);
  const noFlowBeforeAfter = !hasFlow && (data.before || data.after) && !data.inputs.length && !data.outputs.length && !data.data_collection;

  // 범례 + [전체 보기]는 제목 줄 오른쪽 (Figma ver.7 07), 도식을 보는 탭에서만
  const flowShown = hasFlow && tab === "flow";
  const aside = flowShown ? (
    <div className="flex items-center gap-4">
      <WorkflowLegend />
      <WorkflowFullButton onClick={() => setFull(true)} />
    </div>
  ) : undefined;

  return (
    <DetailSection id="workflow" title={data.title} tight={noFlowBeforeAfter ? "snug" : true} aside={aside}>
      {noFlowBeforeAfter ? (
        <BeforeAfterOnly data={data} />
      ) : (
        <div className="flex flex-col gap-6">
          {tabs.length > 1 && <TabSwitch tabs={tabs} active={tab} onChange={setTab} />}
          {(tab === "flow" || !hasFlow) && (
            // 도식 · 입력 / 산출물 · 연동 시스템 사이는 48 (Figma 03-01)
            <div className="flex flex-col gap-12">
              {hasFlow && <WorkflowDiagram stages={data.stages} hitlChips={data.hitl_chips} hitlNote={data.hitl_note} />}
              <IoBlocks data={data} />
              {(data.data_collection || data.integrations) && (
                <div className="flex flex-col gap-6">
                  {data.data_collection && <SysCard label="받는 데이터" text={data.data_collection} icon={<NvDatabaseIcon size={32} />} />}
                  {data.integrations && <SysCard label="연동 시스템" text={data.integrations} icon={<NvAnalyticsPicto size={62} className="absolute max-w-none" />} dashed />}
                </div>
              )}
              {!hasFlow && (data.before || data.after) && <BeforeAfterOnly data={data} />}
            </div>
          )}
          {tab === "before" && data.before && <BeforePanel before={data.before} />}
          {tab === "after" && data.after && <AfterPanel after={data.after} />}
        </div>
      )}
      {hasFlow && <WorkflowFullDialog open={full} onOpenChange={setFull} agentName={agentName} stages={data.stages} hitlChips={data.hitl_chips} hitlNote={data.hitl_note} />}
    </DetailSection>
  );
}
